import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { SEO } from "@/components/site/SEO";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { JsonLd, breadcrumbSchema, webPageSchema } from "@/components/site/Schema";
import { PolicyReviewForm } from "@/components/site/PolicyReviewForm";

const BENEFITS = [
  "Understand exactly what you own",
  "See how the policy functions over time",
  "Identify gaps or misalignments",
  "Receive thoughtful, educational feedback",
];

export default function PolicyReview() {
  return (
    <div className="min-h-screen bg-ivory" data-testid="policy-review-page">
      <SEO
        title="Free Policy Review | Legacy Life & Retirement"
        description="Already have life insurance? Send Shawn King a copy for a complimentary, private, education-first review of what you own, how it works, and whether it supports your goals."
      />
      <JsonLd id="ld-policy-review-breadcrumb" data={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Free Policy Review", path: "/policy-review" }])} />
      <JsonLd id="ld-policy-review-webpage" data={webPageSchema({ path: "/policy-review", name: "Free Policy Review" })} />
      <Header />
      <main>
        <section className="relative bg-ivory pt-[120px] md:pt-[150px] pb-24 md:pb-32 overflow-hidden">
          <div className="pointer-events-none absolute inset-0 grid-lines opacity-40" />
          <div className="container-lr relative">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              {/* Left copy */}
              <motion.div
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="lg:pt-4"
              >
                <div className="flex items-center gap-4">
                  <span className="gold-rule" />
                  <p className="eyebrow">Free Policy Review</p>
                </div>
                <h1 className="mt-6 font-serif font-semibold tracking-display text-legacy text-4xl sm:text-5xl leading-[1.05]">
                  Already have life insurance?
                  <span className="block italic font-normal text-gold mt-1">Let’s look at it together.</span>
                </h1>
                <p className="mt-7 text-slate text-base md:text-lg leading-relaxed">
                  Not sure what type of policy you own, how it works, or whether it aligns with your
                  goals? Send me a copy and I’ll review what you have, how it functions, and whether it
                  supports your overall financial strategy.
                </p>
                <p className="mt-5 text-slate/90 text-sm md:text-base leading-relaxed">
                  Complimentary and private. Shawn reviews each submission personally and reaches out
                  within two business days.
                </p>

                <ul className="mt-10 space-y-4">
                  {BENEFITS.map((b) => (
                    <li key={b} className="flex items-center gap-3 border-t border-taupe/40 pt-4 first:border-t-0 first:pt-0">
                      <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gold/20 text-bronze"><Check size={14} /></span>
                      <span className="text-slate text-[0.95rem]">{b}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Right form */}
              <motion.div
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <PolicyReviewForm />
              </motion.div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
